/** 产品审核结果行工具（展示名优先走前端三语 i18n，接口 FieldName/TabSection 作兜底） */

/**
 * 子项 radio 选中态（Approval_Request_Result.ResultCode）
 * 0 / 2 = 人工勾选约定；4 = AI Pending/review（绿网 review 会带驳回原因）
 * 1 = 未选 / Success
 */
export function isProductApprovalResultRadioSelected(resultCode) {
  const code = String(resultCode ?? '').trim();
  return code === '0' || code === '2' || code === '4';
}

/** 点选 / 取消时写入的 ResultCode */
export const PRODUCT_APPROVAL_RESULT_CODE_SELECTED = '2';
export const PRODUCT_APPROVAL_RESULT_CODE_UNSELECTED = '1';

/** 保存落库：勾选 → 2，未勾选 → 1 */
export function resolveProductApprovalResultCodeForSave(resultCode) {
  return isProductApprovalResultRadioSelected(resultCode)
    ? PRODUCT_APPROVAL_RESULT_CODE_SELECTED
    : PRODUCT_APPROVAL_RESULT_CODE_UNSELECTED;
}

/**
 * 子项是否视为「已驳回」（感叹号 / 「查看 N 条驳回详情」/ 详情弹窗 / 编辑态展示）
 * ResultCode 0 / 2 / 4；不看驳回文案是否有内容。
 */
export function isProductApprovalRejectedRow(row) {
  if (!row) return false;
  return isProductApprovalResultRadioSelected(row.resultCode ?? row.ResultCode ?? '');
}

/**
 * 驳回原因展示：AI 常返回 JSON（对象 / 数组 / 双重转义），只取 Description；否则原样返回。
 * 支持总驳回形态：[{"contentId":"...","rejectReason":"{\"Description\":\"...\"}"}]
 * （与广告 resolveAdRejectReasonText 同思路，并覆盖产品总驳回数组）
 */
export function resolveProductRejectReasonText(raw) {
  if (raw == null || raw === '') return '';

  // 接口偶发已 parse 成对象/数组，不能先 String() 否则变成 [object Object]
  if (typeof raw === 'object') {
    return extractProductRejectDescription(raw);
  }

  let text = String(raw).trim();
  if (!text) return '';
  // 已被错误转成 [object Object] 的脏数据无法再解析
  if (text === '[object Object]' || text === '[object Array]') return '';

  try {
    let parsed = text;
    for (let i = 0; i < 3; i += 1) {
      if (typeof parsed !== 'string') break;
      const t = parsed.trim();
      if (!(t.startsWith('{') || t.startsWith('['))) break;
      parsed = JSON.parse(t);
    }
    if (typeof parsed === 'object' && parsed != null) {
      const fromObj = extractProductRejectDescription(parsed);
      if (fromObj) return fromObj;
    }
  } catch (_) {
    /* 非 JSON，按原文展示 */
  }
  return text;
}

/** 从已解析的对象 / 数组中抽取 Description（可嵌套 rejectReason） */
function extractProductRejectDescription(parsed) {
  if (parsed == null) return '';

  if (Array.isArray(parsed)) {
    const parts = [];
    const seen = new Set();
    parsed.forEach((item) => {
      const part = resolveProductRejectReasonText(
        item == null
          ? ''
          : typeof item === 'object'
            ? (item.rejectReason ?? item.RejectReason ?? item.Description ?? item.description ?? item)
            : item,
      );
      const t = String(part || '').trim();
      if (t && t !== '[object Object]' && !seen.has(t)) {
        seen.add(t);
        parts.push(t);
      }
    });
    return parts.join('；');
  }

  if (typeof parsed === 'object') {
    if (parsed.rejectReason != null || parsed.RejectReason != null) {
      const nested = resolveProductRejectReasonText(
        parsed.rejectReason ?? parsed.RejectReason,
      );
      if (nested) return nested;
    }
    const desc = parsed.Description ?? parsed.description;
    if (desc != null && String(desc).trim() !== '') {
      return String(desc).trim();
    }
  }

  return '';
}

export function resolveProductApprovalContentKind(row) {
  const type = String(row?.contentType || '').trim().toUpperCase();
  if (type === 'IMG' || type === 'VIDEO' || type === 'DOC') return 'file';
  return 'text';
}

/** TabSection（映射视图英文）→ 菜单三语 key */
const PRODUCT_APPROVAL_SECTION_I18N = {
  'Basic Info': 'lbl_EMPBE_ProductInfo_Memu_BasicInfo',
  BasicInfo: 'lbl_EMPBE_ProductInfo_Memu_BasicInfo',
  'Pricing & Trade Info': 'lbl_EMPBE_ProductInfo_Memu_PricingTradeInfo',
  TradeInfo: 'lbl_EMPBE_ProductInfo_Memu_PricingTradeInfo',
  'Product Details': 'lbl_EMPBE_ProductInfo_Memu_ProductDetails',
  ProductDetail: 'lbl_EMPBE_ProductInfo_Memu_ProductDetails',
  'Product Tags': 'lbl_EMPBE_ProductInfo_Memu_ProductTags',
  ProductTag: 'lbl_EMPBE_ProductInfo_Memu_ProductTags',
  ProductTags: 'lbl_EMPBE_ProductInfo_Memu_ProductTags',
};

/** UsageType / DbColumnName → 表单字段三语 key（与各 Popup 标题一致） */
const PRODUCT_APPROVAL_FIELD_I18N = {
  ExhibitNameE: 'lbl_EMPBE_ProductInfo_BasicInfo_ProductName_PlaceOtherEn',
  ExhibitNameS: 'lbl_EMPBE_ProductInfo_BasicInfo_ProductName_PlaceOtherCn',
  ExhibitOtherNameE: 'lbl_EMPBE_ProductInfo_BasicInfo_ProductName_OtherEn',
  ExhibitOtherNameS: 'lbl_EMPBE_ProductInfo_BasicInfo_ProductName_OtherCn',
  ExhibitPhoto: 'lbl_EMPBE_ProductInfo_BasicInfo_ProductPhotos',
  ExhibitVideo: 'lbl_EMPBE_ProductInfo_BasicInfo_ProductVideo',
  ExhibitVideo_Cover: 'lbl_EMPBE_ProductInfo_BasicInfo_ProductVideo',
  ExhibitVideo_Short: 'lbl_EMPBE_ProductInfo_BasicInfo_CompanyShortVideo',
  ExhibitVideo_Short_Cover: 'lbl_EMPBE_ProductInfo_BasicInfo_CompanyShortVideo',
  ModelNum: 'lbl_EMPBE_ProductInfo_BasicInfo_ModelNumberOfTheProduct',
  Ability_Number: 'lbl_EMPBE_ProductInfo_BasicInfo_ProductionCapacity',
  MOQ_Unit_Other: 'lbl_EMPBE_ProductInfo_PricingTradeInformation_MOQ',
  ProductPriceDisplay: 'lbl_EMPBE_SellingPrice',
  DescE: 'lbl_EMPBE_ProductInfo_ProductDetails_ProductDetailsEn',
  DescS: 'lbl_EMPBE_ProductInfo_ProductDetails_ProductDetailsCn',
  DescT: 'lbl_EMPBE_ProductInfo_ProductDetails_ProductDetailsCn',
  CertificationType: 'lbl_EMPBE_CompanyInfo_Certification_certificationType_PlaceOther',
  CertificationName: 'lbl_EMPBE_CompanyInfo_Certification_certificationName_PlaceOther',
  eBooth_Exhibit_Certificate: 'lbl_EMPBE_CompanyInfo_Certification_certificationFile',
  ExhibitCertification: 'lbl_EMPBE_CompanyInfo_Certification_certificationFile',
};

function translateOrFallback(t, key, fallback, params) {
  if (!key || typeof t !== 'function') return fallback || key || '';
  const translated = params ? t(key, params) : t(key);
  if (translated && translated !== key) return translated;
  return fallback || key;
}

/** 驳回详情：分区标题（随当前语种） */
export function resolveProductApprovalSectionLabel(t, tabSection, fallback = '') {
  const section = String(tabSection || '').trim();
  const key = PRODUCT_APPROVAL_SECTION_I18N[section];
  if (key) return translateOrFallback(t, key, section || fallback);
  return section || fallback || '';
}

/** 驳回详情：字段标题（随当前语种） */
export function resolveProductApprovalFieldLabel(t, usageType, fieldName = '') {
  const usage = String(usageType || '').trim();
  const key = PRODUCT_APPROVAL_FIELD_I18N[usage];
  const fallback = String(fieldName || '').trim() || usage || '-';
  if (key) {
    // ProductionCapacity 文案含 {ProductName}，详情弹窗用通用「产品」占位
    return translateOrFallback(t, key, fallback, { ProductName: '' });
  }
  return fallback;
}

function fileNameFromPath(path) {
  const raw = String(path || '').trim();
  if (!raw) return '';
  const parts = raw.split(/[/\\]/);
  return parts[parts.length - 1] || raw;
}

/** Content 多为 {"text":"..."} / {"cdn":"..."}，兼容纯字符串 */
export function parseProductApprovalContentDisplay(content, kind = 'text') {
  const raw = String(content || '').trim();
  if (!raw) return '';
  try {
    const obj = JSON.parse(raw);
    if (obj && typeof obj === 'object') {
      const text = obj.text != null ? String(obj.text).trim() : '';
      if (text) return text;
      const cdn = obj.cdn != null ? String(obj.cdn).trim() : '';
      if (cdn) return kind === 'file' ? fileNameFromPath(cdn) : cdn;
      const fileName = obj.fileName != null ? String(obj.fileName).trim() : '';
      if (fileName) return fileName;
    }
  } catch (e) {
    // not JSON
  }
  return kind === 'file' ? fileNameFromPath(raw) : raw;
}
